import { LoadingService } from 'src/app/core/services/loading.service';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { CurrentUser, User } from 'src/app/core/models/user';
import { UserService } from 'src/app/core/services/open-api/user.service';
import { TranslateService } from '@ngx-translate/core';
import { Table, TableRowSelectEvent } from 'primeng/table';
import { combineLatest, concatMap, from, of, Subscription, tap } from 'rxjs';
import { GroupForm } from '@/core/models/group-form';
import { SelectedItem } from 'src/app/core/models/selected-item.interface';
import { RoleService } from 'src/app/core/services/role.service';
import { GroupService } from 'src/app/core/services/open-api/group.service';
import { Account, UserForm } from 'src/app/core/models/account';
import { StdToasts, ToastService } from '@/core/services/toast.service';

@Component({
    selector: 'app-group',
    templateUrl: './group.component.html',
    // styleUrls: ['./group.component.scss'],
    standalone: false,
})
export class GroupComponent implements OnInit, OnDestroy {
    stateOptions!: SelectItem[];
    selectedView!: string;
    allGroups: GroupForm[] = [];
    currentUser?: CurrentUser;
    showEditDialog = false;
    permissions: SelectItem[] = [];
    user: User = new User();
    licenses?: number;
    displayEmptyPage = true;
    groupName = '';
    isPro: boolean = false;
    selectedGroup?: GroupForm;
    memberOptions: SelectedItem[] = [];
    selectedMembers: SelectedItem[] = [];
    allAvailableMembers: SelectedItem[] = [];
    showNewGroupDialog = false;
    newGroupName: string | undefined = undefined;
    newGroupMembers: SelectedItem[] = [];
    isLoading = false;
    groups: GroupForm[] = [];
    @Output() groupsUpdated = new EventEmitter<GroupForm[]>();
    @ViewChild('dt1') dt?: Table;
    public minLicenceMessage = false;
    private subscriptions = new Subscription();
    private _search?: string;

    set search(text: string) {
        this.dt?.filterGlobal(text, 'contains');
        this._search = text;
    }

    get search(): string {
        return this._search ?? '';
    }

    constructor(
        private userService: UserService,
        private readonly translate: TranslateService,
        private toastService: ToastService,
        public readonly loadingService: LoadingService,
        private confirmationService: ConfirmationService,
        private roleService: RoleService,
        private groupService: GroupService,
    ) {}

    onNewGrDialogHide(): void {
        this.showNewGroupDialog = false;
    }

    deleteGroup(): void {
        if (!this.selectedGroup) return;
        this.loadingService.setLoading(true);
        this.isLoading = true;
        try {
            this.groupService.deleteGroup(this.selectedGroup.id).subscribe((_res) => {
                const index = this.allGroups.findIndex((group) => group.id === this.selectedGroup?.id);
                if (index > -1) {
                    this.allGroups.splice(index, 1);
                }
                this.showEditDialog = false;
                this.loadingService.setLoading(false);
                this.isLoading = false;
                this.updateGroups();

                this.updateLoadingState();

                this.toastService.success('standard_success', 'group_deleted_mesaage');
            });
        } catch (error) {
            console.error('Error deleting group:', error);
            this.toastService.error();
            this.loadingService.setLoading(false);
            this.isLoading = false;
        }
    }

    ngOnInit(): void {
        this.loadingService.setLoading(true);

        this.subscriptions.add(
            combineLatest([this.userService.currentUser$, this.userService.account$]).subscribe(
                async ([currentUser, account]: [CurrentUser, Account]) => {
                    // TODO
                    this.currentUser = currentUser;
                    this.isPro = currentUser.isPro();

                    this.allAvailableMembers = account.users.map((u: UserForm) => ({
                        label: `${u.firstName} ${u.lastName}`,
                        value: {
                            id: u.id,
                            firstName: u.firstName,
                            lastName: u.lastName,
                            email: u.email,
                        },
                    }));
                },
            ),
        );

        this.translate.get(['member_option', 'group_option']).subscribe((res) => {
            this.stateOptions = [
                { label: res['member_option'], value: 'members' },
                { label: res['group_option'], value: 'groups' },
            ];
            this.selectedView = this.stateOptions[0].value;
        });

        this.groupService.getAllGroups().subscribe((res: GroupForm[]) => {
            this.groups = res;
            this.allGroups = res;
            this.displayEmptyPage = this.allGroups.length === 0;
            this.loadingService.setLoading(false);
        });

        this.updateDropdownOptions();
    }

    /**
     * Format member names for display
     * @param members Array of members
     * @returns Formatted string of member names
     */
    formatMemberNames(members: User[] | undefined): string {
        return members !== undefined
            ? members.map((member) => `${member.firstName} ${member.lastName}`).join(', ')
            : '';
    }

    confirmDeletion(event: Event): void {
        this.translate
            .get(['group_delete_confirmation', 'standard_no', 'standard_yes', 'delete_group_confirm_header'])
            .subscribe((res) => {
                const deletionMsg = res['group_delete_confirmation'].replace(
                    '$GROUPNAME$',
                    `'${this.selectedGroup?.name}'`,
                );

                this.confirmationService.confirm({
                    target: event.target as EventTarget,
                    message: `${deletionMsg}?`,
                    header: res['delete_group_confirm_header'],
                    icon: 'pi pi-exclamation-triangle',
                    acceptButtonStyleClass: 'p-button-danger p-button-text',
                    rejectButtonStyleClass: 'p-button-primary p-button-text',
                    acceptIcon: 'none',
                    rejectIcon: 'none',
                    acceptLabel: res['standard_yes'],
                    rejectLabel: res['standard_no'],

                    accept: () => {
                        this.deleteGroup();
                        // this.messageService.add({ severity: 'info', summary: 'Confirmed', detail: 'Record deleted' });
                    },
                });
            });
    }

    // confirmGroupCreate(event: Event) {
    //     // if isGroupCreDisabled is true, the button is disabled
    //     if (this.isGroupCreDisabled()) return;

    //     this.translate
    //         .get([
    //             "group_create_confirm",
    //             "standard_no",
    //             "standard_yes",
    //             "group_changes_confirm_header",
    //         ])
    //         .subscribe((res) => {
    //             this.confirmationService.confirm({
    //                 target: event.target as EventTarget,
    //                 message: res["group_create_confirm"],
    //                 header: res["group_changes_confirm_header"],
    //                 icon: "pi pi-exclamation-triangle",
    //                 acceptIcon: "none",
    //                 rejectIcon: "none",
    //                 acceptLabel: res["standard_yes"],
    //                 rejectLabel: res["standard_no"],
    //                 rejectButtonStyleClass: "p-button-text",
    //                 accept: () => {
    //                     this.createNewGroup();
    //                 },
    //                 reject: () => {
    //                     this.messageService.add({
    //                         severity: "error",
    //                         summary: "Rejected",
    //                         detail: "Aborted",
    //                     });
    //                 },
    //             });
    //         });
    // }

    saveGroupChanges(): void {
        // if (this.isSaveDisabled()) return;
        this.loadingService.setLoading(true);
        this.isLoading = true;
        // uopdate group name in selectedGroup
        if (this.selectedGroup) {
            this.selectedGroup.name = this.groupName;
        }
        const selectedGroupId = this.selectedGroup ? this.selectedGroup.id : -1;

        // Assuming the selectedGroup is always part of userGroups
        // Find the selected group object to update it locally
        const selectedGroupIndex = this.allGroups.findIndex((group) => group.id === selectedGroupId);
        if (selectedGroupIndex === -1) {
            console.error('Selected group not found in userGroups');
            this.loadingService.setLoading(false);
            return;
        }

        // Prepare Observables for each operation, including local state updates
        const changeGroupName$ = this.selectedGroup
            ? this.groupService.changeGroupName(selectedGroupId, this.selectedGroup.name).pipe(
                  tap((_res) => {
                      // Update local state for group name
                      this.allGroups[selectedGroupIndex].name = this.selectedGroup!.name;
                  }),
              )
            : of(null);

        const assignUsers$ = this.groupService
            .assignUsersToGroup(
                selectedGroupId,
                this.selectedMembers.map((member) => member.value.id),
            )
            .pipe(
                tap(() => {
                    // Update local state for group members
                    this.allGroups[selectedGroupIndex].members = this.selectedMembers.map((member) => member.value);
                }),
            );

        // Execute operations sequentially
        from(changeGroupName$)
            .pipe(
                concatMap(() => assignUsers$),
                // concatMap(() => assignTiles$)
            )
            .subscribe({
                next: (res) => console.log('Operation completed:', res),
                error: (error) => {
                    console.error('An error occurred during the operations', error);
                    this.toastService.toast(StdToasts.STANDARD_ERROR);
                    this.loadingService.setLoading(false);
                },
                complete: () => {
                    console.log('All operations completed successfully.');
                    this.toastService.toast(StdToasts.SAVE_SUCCESS);
                    this.updateGroups();
                    // Here you can also refresh your data from the server if needed to ensure UI consistency
                    this.showEditDialog = false;
                    this.loadingService.setLoading(false);
                    this.isLoading = false;
                },
            });
    }

    updateGroups(): void {
        // Suppose the groups are updated here somehow
        const updatedGroups = [...this.groups]; // Just an example update

        // Emit the updated groups
        this.groupsUpdated.emit(updatedGroups);
    }

    get selectedGroupName(): string {
        return this.selectedGroup?.name || 'New Group';
    }

    set selectedGroupName(value: string) {
        if (this.selectedGroup) {
            this.selectedGroup.name = value;
        } else {
            // Handle the case where selectedGroup is not initialized
            // For example, initializing it here if that's appropriate
            //   this.selectedGroup = { name: value };
        }
    }

    onCancel(): void {
        // TODO: Implement logic to cancel changes
        this.showEditDialog = false;
    }

    isSaveDisabled(): boolean {
        // check if group name is not empty
        return this.groupName === '';
    }

    updateDropdownOptions(): void {
        this.memberOptions =
            this.selectedGroup?.members?.map((member) => ({
                label: `${member.firstName} ${member.lastName}`,
                value: member,
            })) ?? [];
        this.selectedMembers = [...this.memberOptions];
        const union = [...this.allAvailableMembers, ...this.selectedMembers];
        const uniqueMemberIds = new Set();

        // Filter combinedMembers to remove duplicates based on member.value.id
        this.memberOptions = union.filter((member) => {
            const memberId = member.value.id;
            if (uniqueMemberIds.has(memberId)) {
                // If the ID is already in the Set, it's a duplicate, so filter it out
                return false;
            } else {
                // If it's not in the Set, add the ID to the Set and keep the member
                uniqueMemberIds.add(memberId);
                return true;
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    getLoadingState(): number {
        let value;
        if (this.loadingService.isLoading()) {
            value = 0;
        } else {
            if (!this.displayEmptyPage) {
                value = 2;
            } else value = 1;
        }
        return value ?? 0;
    }

    createGroup(): void {
        if (this.isPro === true) {
            this.showNewGroupDialog = true;
        }
    }

    onRowSelect(event: TableRowSelectEvent<GroupForm>): void {
        if (!this.isPro) {
            this.selectedGroup = undefined;
            return;
        }
        // only Admins can edit groups
        if (this.currentUser?.hasMultiPermission([...this.roleService.adminRoles])) {
            const selectedGroup = Array.isArray(event.data) ? event.data[0] : event.data;
            this.selectedGroup = selectedGroup;
            this.groupName = selectedGroup?.name ?? '';
            this.showEditDialog = true; // Open the dialog
            this.updateDropdownOptions(); // Update dropdown options based on the selected group
        }
    }

    onNewGrDialogShow(): void {
        if (this.currentUser?.hasMultiPermission([...this.roleService.adminRoles])) {
            // this.tileOptions = this.allAvailableTiles;
            this.memberOptions = this.allAvailableMembers;
        }
    }

    createNewGroup(): void {
        if (this.isGroupCreDisabled()) return;

        const dupGroupName = this.allGroups.find(
            (group) => group.name.toLowerCase().trim() === this.newGroupName?.toLowerCase()?.trim(),
        );
        const validGroupName = this.newGroupName !== undefined && this.newGroupName !== '';

        this.loadingService.setLoading(true);
        this.isLoading = true;
        if (
            validGroupName &&
            !dupGroupName
            // && this.newGroupTiles.length > 0
        ) {
            this.groupService
                .createGroup({
                    name: this.newGroupName ?? '',
                    userIds: this.newGroupMembers.map((member) => member.value.id),
                })
                .subscribe((group: GroupForm) => {
                    // only save the firstName, lastName, email and id
                    // TODO why is this needed?
                    // @ts-expect-error -- The types are incomplete according to type def after the following operattion
                    group.members = group?.members.map((member: User) => {
                        return {
                            id: member.id,
                            firstName: member.firstName,
                            lastName: member.lastName,
                            email: member.email,
                        };
                    });
                    this.allGroups.push(group);
                    this.showNewGroupDialog = false;
                    this.loadingService.setLoading(false);
                    this.updateLoadingState();
                    this.isLoading = false;
                    this.updateGroups();
                    this.toastService.success('standard_success', 'group_create_success');
                    // clear input fields
                    this.newGroupName = undefined;
                    this.newGroupMembers = [];
                });
        } else {
            if (dupGroupName) {
                this.toastService.error('standard_error', 'group_name_duplicate_error');
            }
            this.loadingService.setLoading(false);
            this.isLoading = false;
        }
    }

    updateLoadingState(): void {
        this.displayEmptyPage = this.allGroups.length === 0;
    }

    isGroupCreDisabled(): boolean {
        // prevent click
        return this.newGroupName === undefined || this.newGroupName === '';
    }

    onNewGrCancel(): void {
        // TODO: Implement logic to cancel creating a new group
        this.showNewGroupDialog = false;
    }
}
